import { parseDuration } from '@utils/time/timeUtils';
import { truncateWords } from '@utils/text/textUtils';

export const RECENT_EPISODE_LIMIT = 8;
export const EPISODE_SUMMARY_LIMIT = 40;

export function getEpisodeNumber(episodeId) {
  const match = String(episodeId || '').match(/^\d+/);
  return match ? match[0].padStart(3, '0') : '';
}

export function getEpisodeArtPath(episodeId) {
  const episodeNumber = getEpisodeNumber(episodeId);
  return episodeNumber ? `/episode-art/${episodeNumber}.jpg` : '';
}

/**
 * Convert parsed feed data into the application episode model.
 */
export function normalizeEpisodeFeed(feed) {
  if (!feed || !Array.isArray(feed.episodes)) {
    throw new Error('Invalid episode feed');
  }

  const feedImage = feed.image || '';
  const episodes = [...feed.episodes].reverse().map((episode) => {
    const localArtPath = getEpisodeArtPath(episode.id);
    const repeatsFeedArt = Boolean(episode.image) && episode.image === feedImage;
    const image = localArtPath || (repeatsFeedArt ? '' : episode.image);

    return localArtPath || repeatsFeedArt ? { ...episode, image } : episode;
  });

  return {
    episodes,
    feedMetadata: {
      title: feed.title || '',
      description: feed.description || '',
      link: feed.link || '',
      image: feed.image || ''
    }
  };
}

export function findEpisodeById(episodes, episodeId) {
  return episodes.find((episode) => episode.id === episodeId);
}

export function getRecentEpisodes(episodes, limit = RECENT_EPISODE_LIMIT) {
  return episodes.slice(0, limit);
}

export function getEpisodeProgress(episode, currentPosition = 0) {
  const totalDuration = parseDuration(episode?.duration);
  const position = Math.max(0, currentPosition || 0);
  const progressPercent = totalDuration > 0
    ? Math.min(100, (position / totalDuration) * 100)
    : 0;

  return {
    currentPosition: position,
    totalDuration,
    progressPercent,
    isStarted: position > 0,
    isCompleted: progressPercent > 95
  };
}

export function shouldExpandEpisodeSummary(summary) {
  return (summary || '').split(/\s+/).filter(Boolean).length > EPISODE_SUMMARY_LIMIT;
}

export function getEpisodeSummary(summary, expanded, maxWords = EPISODE_SUMMARY_LIMIT) {
  const text = summary || '';
  return expanded || !shouldExpandEpisodeSummary(text)
    ? text
    : truncateWords(text, maxWords);
}
